import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { isEmpty } from "lodash";

import { Backdrop, CircularProgress, makeStyles } from "@material-ui/core";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import {
  CarrierType,
  FilePaths,
  NoteType,
  OutboundState,
  OutboundStatus,
  PhotoStatus,
  TransferType,
} from "../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../utils/fileUtils";
import { validateAppointment } from "../../../utils/dateUtils";
import { normalizeValue } from "../../../utils/utils";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import {
  autogenPickupNumber,
  calcTotalQuantity,
  formValidate,
  getAllInventories,
  validateOutboundBeforeCreate,
  validateOutboundUnicity,
  formatOutboundDuplicateSaveMessage,
  isInternalTransfer,
} from "./OutboundUtils";
import { resolveCreateBillToAccountId } from "../accounts/AccountUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const ValidateCreateButton = (props) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmContent, setConfirmContent] = useState("");
  const {
    fromAccount,
    toAccount,
    operatingAccount,
    selectedTimeslot,
    photos = [],
    setAllInventories,
    setIsLoaded,
  } = props;
  const isDisabled = photos?.some((p) => p.status === PhotoStatus.UPLOADING);

  const handleValidateSave = async () => {
    const formState = form.getState();
    const formData = formState.values;
    const tempMsg = [];
    setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error
    debugger;

    const transfer = isEmpty(formData.transfer) ? null : formData.transfer[0];
    const pickList = formData.shipmentLineItems?.items;

    if (
      transfer === TransferType.INTERNAL &&
      (!pickList || pickList.length === 0)
    ) {
      notify(
        "Cannot save internal transfer without inventory items",
        "warning",
      );
      return;
    }

    const isValid = formValidate(form, notify);
    if (isValid) {
      try {
        setIsSaving(true);
        // Stale outbound+lineItem+inventory validation:
        const { staleErrors } = await validateOutboundBeforeCreate(
          formState,
          notify,
        );

        // If any staleErrors: force a refresh via Confirm
        if (staleErrors.length > 0) {
          getAllInventories(
            formData.accountId,
            formData.containerPickup,
            setAllInventories,
            setIsLoaded,
            notify,
          );
          setConfirmContent(
            <div>
              <p>Data was modified by another user while you were editing:</p>
              <ul>
                {staleErrors.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
              <p>Please apply your edits again.</p>
            </div>,
          );
          setOnConfirm(() => () => {
            setOpen(false);
          });
          setOpen(true);
          setIsSaving(false);
          return;
        }

        const duplicateNumbers = await validateOutboundUnicity({
          ...formData,
          account: fromAccount,
        });
        if (duplicateNumbers.length > 0) {
          tempMsg.push(formatOutboundDuplicateSaveMessage(duplicateNumbers));
        }

        if (selectedTimeslot) {
          const isBooked = await validateAppointment(selectedTimeslot);
          if (isBooked) {
            tempMsg.push(
              `${selectedTimeslot.time} is no longer available. Please select a new time.`,
            );
            setOnConfirm(null); // Prevent confirmation if there's an error
          }
        }
      } catch (error) {
        logError(
          "outboundShipment:ValidateCreateButton:handleValidateSave",
          error,
          notify,
        );
        setIsSaving(false);
      }
    } else {
      setIsSaving(false);
    }

    if (tempMsg.length === 1) {
      setConfirmContent(tempMsg[0]);
      setOpen(true);
      setIsSaving(false);
    } else if (tempMsg.length > 1) {
      setConfirmContent(
        <ul>
          {tempMsg.map((msg) => (
            <li>{msg}</li>
          ))}
        </ul>,
      );
      setOpen(true);
      setIsSaving(false);
    } else handleSave();
  };

  const handleDialogConfirm = () => handleSave();
  const handleDialogClose = () => setOpen(false);

  const [onConfirm, setOnConfirm] = useState(() => handleDialogConfirm);

  const handleSave = async () => {
    setOpen(false);
    props.handleSubmitWithRedirect(); // Ensures transform is applied
  };

  const transformOutbound = async (data) => {
    // INTERNAL: billToAccountId = user-selected operatingAccount (e.g. ReadyMix)
    // BROKERED: billToAccountId = account's defaultBillToAccountId (e.g. Lotz)
    // CUSTOMER: billToAccountId = fromAccount.id (never use defaultBillToAccountId)
    data.billToAccountId = resolveCreateBillToAccountId(
      fromAccount,
      operatingAccount?.id,
    );
    data.accountName =
      operatingAccount?.businessName || fromAccount.businessName;

    // A create can carry a gate arrival ("Time Arrived") — that is an ARRIVED
    // shipment, not a PENDING one. Placement can only arrive via transfers.
    data.status = data.placementDateTime
      ? OutboundStatus.PLACED
      : data.arrivalDateTime
        ? OutboundStatus.ARRIVED
        : OutboundStatus.PENDING;

    data.createDateTime = new Date();
    data.scheduledDateTime = data.scheduledDateTime
      ? data.scheduledDateTime
      : null;
    data.arrivalDateTime = data.arrivalDateTime ? data.arrivalDateTime : null;
    data.placementDateTime = data.placementDateTime
      ? data.placementDateTime
      : null;
    data.endDateTime = data.endDateTime ? data.endDateTime : null;
    data.departureDateTime = data.departureDateTime
      ? data.departureDateTime
      : null;

    data.railCarNumber = data.railCarNumber ? data.railCarNumber : "";
    data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);
    data.mustTarp = data.mustTarp ? data.mustTarp : false;
    data.scaleWeightUnits = data.weightUnits;

    if (fromAccount.generatePickupNumber) {
      data.pickupNumber = await autogenPickupNumber(fromAccount);
    } else {
      data.pickupNumber = data.pickupNumber ? data.pickupNumber : "";
    }

    if (data.shipTo?.state) data.shipTo.state = data.shipTo.state.toUpperCase();
    if (data.shipTo?.zipCode)
      data.shipTo.zipCode = data.shipTo.zipCode.toUpperCase();

    data.history = [
      {
        text: `Created Outbound.`,
        by: window.appUser?.fullName,
        date: data.createDateTime,
        type: NoteType.SHIPMENT,
      },
    ];

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        element.type = NoteType.GENERIC;
        element.date = new Date();
        element.by = window.appUser?.fullName;
      });
    } else data.notes = undefined;

    // TODO
    // check if still used and needed???
    if (data.shipmentLineItems?.items?.length > 0) {
      debugger;
      data.areaId = data.shipmentLineItems.items[0]?.inventory.areaId;
      data.zoneId = data.shipmentLineItems.items[0]?.inventory.zoneId;
      data.reloadYardCommodityId =
        data.shipmentLineItems.items[0]?.inventory.reloadYardCommodityId;
    }

    return data;
  };

  const transformTransfer = (data) => {
    debugger;
    data.createDateTime = new Date();

    // default billToAccountId = fromAccount on transfer
    data.billToAccountId = fromAccount.id;
    data.accountName = fromAccount.businessName;
    data.transferToAccountName = toAccount.businessName;

    if (data.transfer === TransferType.INTERNAL) {
      data.scheduledDateTime = null;
      data.arrivalDateTime = data.createDateTime;
      data.placementDateTime = data.createDateTime;
      data.startDateTime = data.createDateTime;
      data.endDateTime = data.createDateTime;
      data.departureDateTime = data.createDateTime;

      data.status = OutboundStatus.RELEASED;
      data.state = OutboundState.COMPLETED;
    } else {
      data.status = OutboundStatus.PENDING;
      data.scheduledDateTime = data.scheduledDateTime
        ? data.scheduledDateTime
        : null;
      data.arrivalDateTime = data.arrivalDateTime ? data.arrivalDateTime : null;
      data.placementDateTime = data.placementDateTime
        ? data.placementDateTime
        : null;
      data.startDateTime = data.startDateTime ? data.startDateTime : null;
      data.endDateTime = data.endDateTime ? data.endDateTime : null;
      data.departureDateTime = data.departureDateTime
        ? data.departureDateTime
        : null;
    }

    data.carrierType = CarrierType.TRUCK;
    data.railCarNumber = "TRANSFER";
    data.poNumber = data.poNumber ? data.poNumber : "TRANSFER";
    data.consigneePoNumber = data.consigneePoNumber
      ? data.consigneePoNumber
      : "";
    data.pickupNumber = "TRANSFER";

    data.normalizedPoNumber = normalizeValue(data.poNumber);
    data.normalizedConsigneePoNumber = normalizeValue(data.consigneePoNumber);
    data.normalizedPickupNumber = normalizeValue(data.pickupNumber);
    data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);

    // Same marker the edit form writes (see outboundShipments/
    // DialogSaveButton.js): carrierName, not the legacy `carrier` scalar, and
    // no carrier record for the yard's own move.
    data.carrierName = "TRANSFER";
    data.carrierId = null;
    data.mustTarp = false;

    data.history = [
      {
        text: `Inventory Transfer of ${calcTotalQuantity(data)} ${data.totalUOM} from ${data.accountName} to ${toAccount.businessName}`,
        by: window.appUser?.fullName,
        date: data.createDateTime,
        type: NoteType.INVENTORY,
      },
    ];

    if (data.notes) {
      data.notes.forEach((element) => {
        element.type = NoteType.GENERIC;
        element.date = data.createDateTime;
        element.by = window.appUser?.fullName;
      });
    }

    delete data.shipTo;

    return data;
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };
    debugger;

    // Parent identity minted client-side; AppSync no longer creates the record.
    data.id = uuidv4();
    data.owner = window.appUser.owner;
    data.createdAt = new Date().toISOString();
    data.updatedAt = data.createdAt;

    data.active = "true";
    data.groups = window.appUser?.groups;
    data.reloadYardId = window.appUser?.reloadYardId;
    data.invoiceId = " "; // required for secondary index

    data.poNumber = data.poNumber ? data.poNumber : "";
    data.consigneePoNumber = data.consigneePoNumber
      ? data.consigneePoNumber
      : "";
    data.pickupNumber = data.pickupNumber ? data.pickupNumber : "";

    data.normalizedPoNumber = normalizeValue(data.poNumber);
    data.normalizedConsigneePoNumber = normalizeValue(data.consigneePoNumber);
    data.normalizedPickupNumber = normalizeValue(data.pickupNumber);

    try {
      data.transfer = isEmpty(data.transfer) ? null : data.transfer[0];

      if (data.transfer) transformTransfer(data);
      else await transformOutbound(data);

      data.customFields = getCustomFieldsToSave(
        data.customFields,
        (operatingAccount || fromAccount)?.outboundCustomFields,
      );

      await Promise.all([
        saveRemoveFiles(
          undefined,
          data,
          "attachments",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
        saveRemoveFile(
          undefined,
          data,
          "bolFile",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
        saveRemoveFiles(
          undefined,
          data,
          "photos",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
      ]);
    } catch (err) {
      logError("outboundShipment:ValidateCreateButton:transform", err, notify);
      return Promise.reject();
    }

    // clear extra form fields (saveAddress is kept: the builder needs it;
    // the dataProvider create branch strips it before persisting)
    delete data.customFieldSelect;

    // clear inventorySearchDialog filters
    delete data.lotType;
    delete data.searchArea;
    delete data.searchTerm;
    delete data.showAll;

    // Sanitizing MOVES to the dataProvider branch's return: the lambda-body
    // builder needs the unsanitized record (shipmentLineItems.items etc.).
    data.__saveContext = { photos, selectedTimeslot, toAccount };

    return data;
  };

  const onSuccess = ({ data }) => {
    // Assembly + lambda write now happen inside the dataProvider create branch
    // (createLambdaOutboundShipment). This fires only after that call resolves,
    // so a failed lambda never redirects and never leaves an orphan parent.
    try {
      if (isInternalTransfer(data)) {
        notify("Transfer processed successfully.");
      } else {
        notify("Outbound created successfully", "success");
      }
      redirect("/outboundShipments");
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    const message = error.errors[0]?.message || "";
    if (message?.startsWith("The variables input")) {
      const formState = form.getState();
      logError(
        "outboundShipment:ValidateCreateButton:data",
        formState.values,
        false,
      );
    }
    logError(
      "outboundShipment:ValidateCreateButton:onFailure",
      error,
      notify,
      redirect,
    );
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        label="Save"
        submitOnEnter={false}
        undoable={false}
        saving={isSaving || isDisabled}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={confirmContent}
        onConfirm={onConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
